/**
 * Shared helpers for report row/card.
 */
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  REPORT_REMOTE_KNOWN_PARTICIPANT_ROLES,
  REPORT_REMOTE_PARTICIPANT_ROLE_GROUPS,
  REPORT_REMOTE_TAB,
  REMOTE_REPORT_TAB_SESSION_TYPES,
  type ReportRemoteFilters as Filters,
} from '@/components/reports/report-configs';
import type { ManagedByValue } from '@/components/common/ManagedByMultiSelect';
import type { Program } from '@/types/program.types';
import type {
  ReportAnimator,
  ReportListItem,
  ReportListParams,
  ReportParticipant,
  ReportTab as Tab,
} from '@/types/remote/report.types';
import { getErrorMessage } from '@/utils/errors-utils';
import { toLocalDateString } from '@/utils/format-date-utils';
import { resolveListDateRange } from '@/utils/list-date-range-utils';
import { getRoleLabel, roleKind } from '@/utils/role-label-utils';
export {
  getEffectiveReportSessionTypes,
  getReportStatusKeys,
  reportIcon,
  resolveReportPermissions,
  sessionTypeToReportTab,
  toReportTab,
} from '@/utils/common-utils';

/** Role-split participants used by {@link ReportRow} / {@link ReportCard}. */
export interface DerivedReportParticipants {
  tutors: ReportParticipant[];
  students: ReportParticipant[];
  animator: ReportAnimator | undefined;
  /** RI (information_meeting): tutors double as participants */
  infoParticipants: ReportParticipant[];
  /** Welcome meeting: all participants */
  welcomeParticipants: ReportParticipant[];
}

/** Edit / validate disabled flags + tooltips. */
export interface ReportActionState {
  editDisabled: boolean;
  editTooltip: string;
  validateDisabled: boolean;
  validateTooltip: string;
}

/** True on the "all reports" tab. */
export function isAllReportsTab(tab: Tab): boolean {
  return tab === REPORT_REMOTE_TAB.all;
}

/** True on the tutoring-reports tab. */
export function isTutoringReportTab(tab: Tab): boolean {
  return tab === REPORT_REMOTE_TAB.tutoring;
}

/** True on the information-meeting reports tab. */
export function isInformationReportTab(tab: Tab): boolean {
  return tab === REPORT_REMOTE_TAB.information;
}

/** True on the welcome-meeting reports tab. */
export function isWelcomeReportTab(tab: Tab): boolean {
  return tab === REPORT_REMOTE_TAB.welcome;
}

/** True on information or welcome meeting tabs. */
export function isMeetingReportTab(tab: Tab): boolean {
  return isInformationReportTab(tab) || isWelcomeReportTab(tab);
}

/** Column headers for the current report tab (type-specific extras). */
export function reportTableHeaders(tab: Tab): string[] {
  const headers: string[] = ['Date'];
  if (isAllReportsTab(tab)) headers.push('Type');
  else if (isTutoringReportTab(tab))
    headers.push(getRoleLabel('tutor'), getRoleLabel('student'), 'Type');
  else if (isMeetingReportTab(tab)) {
    headers.push('Animateur', isInformationReportTab(tab) ? 'Participants' : 'Élève(s)');
  }
  headers.push(
    isTutoringReportTab(tab) ? 'Statut rapport' : 'Statut synthèse',
    'Statut scoring',
    'Dernière modif',
    'Actions',
  );
  return headers;
}

/** Expanded-row colspan matching the tab's column count. */
export function reportTableExpandedColSpan(tab: Tab): number {
  if (isTutoringReportTab(tab)) return 8;
  if (isAllReportsTab(tab)) return 6;
  return 7;
}

/** Split tutors / students / animator; RI tutors double as participants. */
export const deriveReportParticipants = (report: ReportListItem): DerivedReportParticipants => {
  const participants = report.participants ?? [];
  const tutors = participants.filter((p) => roleKind(p.role) === 'tutor');
  const students = participants.filter((p) => roleKind(p.role) === 'student');
  // For RI, the tutors who signed up are the "participants" in the display
  const infoParticipants = tutors;
  const welcomeParticipants = participants;

  return {
    tutors,
    students,
    animator: report.animator,
    infoParticipants,
    welcomeParticipants,
  };
};

/** Validated or BOTH_ABSENT locks edit; validate only when status is `saisi`. */
export const getReportActionState = (report: ReportListItem): ReportActionState => {
  const isPairAbsent = report.sessionStatus === 'BOTH_ABSENT';
  const editDisabled = report.compteRenduStatus === 'valide' || isPairAbsent;
  const validateDisabled = report.compteRenduStatus !== 'saisi';

  let editTooltip: string;
  if (isPairAbsent) {
    editTooltip = getErrorMessage('reportUnavailablePairAbsent');
  } else if (editDisabled) {
    editTooltip = getErrorMessage('validatedReportLocked');
  } else {
    editTooltip = 'Modifier le compte rendu';
  }

  return {
    editDisabled,
    editTooltip,
    validateDisabled,
    validateTooltip: validateDisabled
      ? getErrorMessage('reportMustBeSubmittedToValidate')
      : 'Valider le compte rendu',
  };
};

/** Formats a session date (YYYY-MM-DD or ISO) for display (e.g. "3 jan. 2026"). */
export const formatReportSessionDate = (sessionDate: string | Date): string =>
  format(new Date(sessionDate), 'd MMM yyyy', { locale: fr });

/** Formats lastModifiedAt (ISO) for display (e.g. "3 jan. 2026 14:30"). Returns "—" if absent. */
export const formatReportLastModified = (lastModifiedAt: string | null | undefined): string =>
  lastModifiedAt ? format(new Date(lastModifiedAt), 'd MMM yyyy HH:mm', { locale: fr }) : '—';

/** All-tab types from RBAC, then selected chips; single-tab uses that tab's types. */
export function resolveSessionTypeParam(
  activeTab: Tab,
  allowedTabIds: Tab[] | undefined,
  filters: Filters,
): string | undefined {
  if (!isAllReportsTab(activeTab)) {
    const sessionTypes = REMOTE_REPORT_TAB_SESSION_TYPES[activeTab];
    return sessionTypes && sessionTypes.length > 0 ? sessionTypes.join(',') : undefined;
  }

  const nonAllTabs = (allowedTabIds ?? []).filter((id) => !isAllReportsTab(id));
  const allowedTypes =
    nonAllTabs.length > 0
      ? nonAllTabs.flatMap((id) => REMOTE_REPORT_TAB_SESSION_TYPES[id] ?? [])
      : ['individual_tutoring', 'information_meeting', 'welcome_meeting', 'workshop'];

  const selected =
    (filters.sessionTypes?.length ?? 0) > 0
      ? filters.sessionTypes.filter((t) => allowedTypes.includes(t))
      : ['individual_tutoring'];

  const effective = selected.filter((t) => allowedTypes.includes(t));

  let types: string[];
  if (effective.length > 0) {
    types = effective;
  } else if (allowedTypes.includes('individual_tutoring')) {
    types = ['individual_tutoring'];
  } else {
    types = allowedTypes;
  }

  return types.length > 0 ? types.join(',') : undefined;
}

/** Map period chips / custom dates to API `periodFrom` / `periodTo`. */
export function buildDateParams(filters: Filters): {
  periodFrom?: string;
  periodTo?: string;
} {
  const { dateFrom, dateTo } = resolveListDateRange(filters, { periodEnd: 'endOfPeriod' });
  return {
    periodFrom: dateFrom ? toLocalDateString(dateFrom) : undefined,
    periodTo: dateTo ? toLocalDateString(dateTo) : undefined,
  };
}

/** `undefined` = no filter; `all` / `unmanaged` / ids are sent as-is. */
export function resolveManagedByParam(value: ManagedByValue | undefined): string[] | undefined {
  if (value === 'all') return ['all'];
  if (value === 'unmanaged') return ['unmanaged'];
  if (Array.isArray(value) && value.length > 0) return value;
  return undefined;
}

/** Map UI filters + tab to remote report list query params. */
export function buildRemoteReportListParams(
  filters: Filters,
  options: {
    page: number;
    pageSize: number;
    activeTab: Tab;
    allowedTabIds?: Tab[];
  },
): ReportListParams {
  const params: ReportListParams = {
    page: options.page,
    pageSize: options.pageSize,
  };

  const sessionTypeParam = resolveSessionTypeParam(
    options.activeTab,
    options.allowedTabIds,
    filters,
  );
  if (sessionTypeParam) params.sessionType = sessionTypeParam;

  if (filters.reportStatus?.length > 0) {
    params.compteRenduStatus = filters.reportStatus.join(',');
  }
  if (filters.scoringStatus?.length > 0) {
    params.scoringStatus = filters.scoringStatus.join(',');
  }

  const { periodFrom, periodTo } = buildDateParams(filters);
  if (periodFrom) params.periodFrom = periodFrom;
  if (periodTo) params.periodTo = periodTo;

  if (filters.search) params.search = filters.search;
  if (filters.tutorUserId) params.tutorUserId = filters.tutorUserId;
  if (filters.studentUserId) params.studentUserId = filters.studentUserId;
  if (filters.animatorUserId) params.animatorUserId = filters.animatorUserId;
  if (filters.programId) params.programId = filters.programId;
  if (filters.schoolYearId) params.schoolYearId = filters.schoolYearId;
  const managedByParam = resolveManagedByParam(filters.managedByIds);
  if (managedByParam) params.managedByIds = managedByParam;

  return params;
}

/** True when any remote-report chip / date / search filter is set. */
export function hasRemoteReportActiveFilters(filters: Filters): boolean {
  return (
    filters.reportStatus.length > 0 ||
    filters.scoringStatus.length > 0 ||
    filters.dateRange !== 'all' ||
    !!filters.customDateFrom ||
    !!filters.customDateTo ||
    !!filters.search ||
    !!filters.tutorUserId ||
    !!filters.studentUserId ||
    !!filters.animatorUserId ||
    !!filters.managedByIds ||
    !!filters.programId ||
    !!filters.schoolYearId
  );
}

/** Deduped program select options (`name` / `label` / `code` fallback). */
export function toRemoteReportProgramSelectOptions(
  programs: Array<Pick<Program, 'id' | 'name' | 'label' | 'code'>>,
): Array<{ value: string; label: string }> {
  const seen = new Set<string>();
  return programs.flatMap((p) => {
    const id = p.id?.trim();
    if (!id || seen.has(id)) return [];
    seen.add(id);
    return [{ value: id, label: p.name ?? p.label ?? p.code ?? id }];
  });
}

/** Toggle `value` in a list (used by filter chips). */
export function toggleListItem<T>(list: T[] | undefined, value: T): T[] {
  const current = list ?? [];
  return current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
}

/** Split session participants into tutor / student / parent / other buckets. */
export function partitionSessionParticipants<T extends { role: string }>(
  participants: T[],
): {
  tutors: T[];
  students: T[];
  parents: T[];
  others: T[];
} {
  return {
    tutors: participants.filter((participant) =>
      REPORT_REMOTE_PARTICIPANT_ROLE_GROUPS.tutor.includes(participant.role as never),
    ),
    students: participants.filter((participant) =>
      REPORT_REMOTE_PARTICIPANT_ROLE_GROUPS.student.includes(participant.role as never),
    ),
    parents: participants.filter((participant) =>
      REPORT_REMOTE_PARTICIPANT_ROLE_GROUPS.parent.includes(participant.role as never),
    ),
    others: participants.filter(
      (participant) => !REPORT_REMOTE_KNOWN_PARTICIPANT_ROLES.includes(participant.role),
    ),
  };
}
